import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Button, Modal } from "antd";
import { CloseOutlined, RightOutlined } from "@ant-design/icons";
import { useLocation, useNavigate } from "react-router-dom";
import logoMark from "@assets/images/image170.png";
import { getReleaseNotesReq } from "./requests";
import "./release-note-popup.css";

const CUSTOMER_AUDIENCE = "customer";
const CHANGE_ORDER = ["new", "improved", "fixed", "security"];
const MAX_PREVIEW_CHANGES = 5;

const CHANGE_LABELS = {
  new: "New Features",
  improved: "Improved",
  fixed: "Fixed",
  security: "Security",
};

const isTruthy = (value) => value === true || value === 1 || value === "1" || value === "true";

const normalizePayload = (response) => {
  if (Array.isArray(response)) return response;
  if (Array.isArray(response?.data?.release_notes)) return response.data.release_notes;
  if (Array.isArray(response?.release_notes)) return response.release_notes;
  if (Array.isArray(response?.data)) return response.data;

  return [];
};

const releaseDateValue = (value) => {
  if (!value) return null;

  const date = new Date(String(value).slice(0, 10));
  return Number.isNaN(date.getTime()) ? null : date;
};

const isCustomerSafeRelease = (note) => {
  const releaseDate = releaseDateValue(note?.release_date);
  const today = new Date();
  today.setHours(23, 59, 59, 999);

  return note?.status === "published"
    && isTruthy(note?.show_in_changelog)
    && !isTruthy(note?.internal_only)
    && releaseDate
    && releaseDate <= today;
};

const customerChanges = (changes = []) => (
  (Array.isArray(changes) ? changes : [])
    .filter((change) => !change?.audience || change.audience === CUSTOMER_AUDIENCE)
    .filter((change) => change?.text)
);

const normalizeReleaseNotes = (response) => (
  normalizePayload(response)
    .filter(isCustomerSafeRelease)
    .map((note) => ({
      ...note,
      changes: customerChanges(note.changes),
    }))
    .filter((note) => note.changes.length > 0)
);

const storageKey = (version, type) => `klok_release_popup_${type}_${version || "latest"}`;

const getCookieValue = (name) => (
  document.cookie
    .split("; ")
    .find((row) => row.startsWith(`${encodeURIComponent(name)}=`))
    ?.split("=")[1]
);

const setCookieValue = (name, value, maxAgeSeconds = 60 * 60 * 24 * 365) => {
  document.cookie = `${encodeURIComponent(name)}=${encodeURIComponent(value)}; path=/; max-age=${maxAgeSeconds}; SameSite=Lax`;
};

const hasSeenRelease = (version) => (
  getCookieValue(storageKey(version, "seen")) === "1"
  || window.localStorage.getItem(storageKey(version, "seen")) === "1"
  || window.sessionStorage.getItem(storageKey(version, "closed")) === "1"
);

const markSeen = (version) => {
  const key = storageKey(version, "seen");
  window.localStorage.setItem(key, "1");
  setCookieValue(key, "1");
};

const markClosedForSession = (version) => {
  window.sessionStorage.setItem(storageKey(version, "closed"), "1");
};

const groupedPreviewChanges = (changes = []) => {
  const groups = CHANGE_ORDER.reduce((result, type) => ({ ...result, [type]: [] }), {});
  let visibleCount = 0;

  changes.forEach((change) => {
    if (visibleCount >= MAX_PREVIEW_CHANGES) return;

    const type = CHANGE_ORDER.includes(change.type) ? change.type : "improved";
    groups[type].push(change);
    visibleCount += 1;
  });

  return {
    groups,
    visibleCount,
    remainingCount: Math.max(changes.length - visibleCount, 0),
  };
};

const ReleaseNotePopup = () => {
  const [release, setRelease] = useState(null);
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  const isChangelogPage = location.pathname.replace(/\/+$/, "") === "/whats-new";

  useEffect(() => {
    let isMounted = true;

    const loadPopupRelease = async () => {
      if (isChangelogPage) return;

      try {
        const response = await getReleaseNotesReq({});
        const popupRelease = normalizeReleaseNotes(response)
          .find((note) => isTruthy(note.trigger_popup) && !hasSeenRelease(note.version));

        if (!isMounted || !popupRelease) return;

        setRelease(popupRelease);
        setOpen(true);
      } catch (error) {
        if (process.env.NODE_ENV === "development") {
          // The announcement is helpful, not required for daily work.
          // Keep API issues quiet for customers while preserving dev visibility.
          console.warn("Release announcement could not be loaded.", error);
        }
      }
    };

    loadPopupRelease();

    return () => {
      isMounted = false;
    };
  }, [isChangelogPage]);

  useEffect(() => {
    if (isChangelogPage && open) {
      setOpen(false);
    }
  }, [isChangelogPage, open]);

  const preview = useMemo(
    () => groupedPreviewChanges(release?.changes || []),
    [release?.changes]
  );

  const closeForSession = useCallback(() => {
    if (release?.version) {
      markClosedForSession(release.version);
    }

    setOpen(false);
  }, [release?.version]);

  const acknowledgeRelease = useCallback(() => {
    if (release?.version) {
      markSeen(release.version);
    }

    setOpen(false);
  }, [release?.version]);

  const openFullChangelog = useCallback(() => {
    if (release?.version) {
      markClosedForSession(release.version);
    }

    setOpen(false);
    navigate("/whats-new");
  }, [navigate, release?.version]);

  if (!release) return null;

  return (
    <Modal
      centered
      className="release-popup"
      footer={ null }
      keyboard={ false }
      maskClosable={ false }
      onCancel={ closeForSession }
      open={ open && !isChangelogPage }
      width={ 520 }
      closable={ false }
    >
      <section className="release-popup__panel" aria-labelledby="release-popup-title">
        <header className="release-popup__header">
          <button
            type="button"
            className="release-popup__close"
            onClick={ closeForSession }
            aria-label="Close release announcement"
          >
            <CloseOutlined />
          </button>
          <img className="release-popup__logo" src={ logoMark } alt="KLOK" />
          <span className="release-popup__eyebrow">What's New</span>
          <h2 id="release-popup-title">{ release.headline || "New Version Available" }</h2>
          <p>v{ release.version }</p>
        </header>

        <div className="release-popup__body">
          { CHANGE_ORDER.map((type) => {
            const changes = preview.groups[type] || [];

            if (!changes.length) return null;

            return (
              <section className="release-popup__group" key={ type }>
                <h3>{ CHANGE_LABELS[type] }</h3>
                <ol>
                  { changes.map((change, index) => (
                    <li key={ change.id || `${type}-${index}` }>
                      <span className="release-popup__number">{ index + 1 }</span>
                      <span className="release-popup__change-text">
                        { change.module ? <strong>{ change.module }</strong> : null }
                        { change.module ? " - " : null }
                        { change.text }
                      </span>
                    </li>
                  )) }
                </ol>
              </section>
            );
          }) }

          { preview.remainingCount > 0 ? (
            <p className="release-popup__more">
              +{ preview.remainingCount } more update{ preview.remainingCount === 1 ? "" : "s" } in the full changelog
            </p>
          ) : null }

          <button
            type="button"
            className="release-popup__changelog"
            onClick={ openFullChangelog }
          >
            Read the full changelog
            <RightOutlined aria-hidden="true" />
          </button>

          <Button
            block
            className="release-popup__primary"
            type="primary"
            onClick={ acknowledgeRelease }
          >
            Got it
          </Button>

          <button
            type="button"
            className="release-popup__quiet"
            onClick={ acknowledgeRelease }
          >
            Don't show this again
          </button>
        </div>
      </section>
    </Modal>
  );
};

export default ReleaseNotePopup;
